import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import PassagePicker from '../components/PassagePicker';
import DashboardHeader from '../components/DashboardHeader';
import { useAutosizeTextarea } from '../hooks/useAutosizeTextarea';
import { passageIdFromLocationSearch } from '../utils/passageUrl';
import { cutTextBySentencePunctuation } from '../utils/textProcess';
import { formatChinesePunctuation } from '../utils/chinesePunctuation';
import './Dashboard.css';

const LINE_TEXTAREA_STYLE = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 14,
  lineHeight: 1.5,
};

/** ~6 lines at 14px and line-height 1.5, plus padding budget */
const MIN_TEXTAREA_HEIGHT_PX = 120;

const SentenceParagraphRow = ({ row, rowIndex, updateRow }) => {
  const srcRef = useRef(null);
  const trRef = useRef(null);
  const idxSrcRef = useRef(null);
  const idxTrRef = useRef(null);

  const sourceLineCount = row.sourceText.split('\n').length;
  const translationLineCount = row.translationText.split('\n').length;
  const lineMismatch = sourceLineCount !== translationLineCount;
  const sourceIndexText = Array.from(
    { length: Math.max(sourceLineCount, 1) },
    (_, i) => String(i + 1)
  ).join('\n');
  const translationIndexText = Array.from(
    { length: Math.max(translationLineCount, 1) },
    (_, i) => String(i + 1)
  ).join('\n');
  const lineCountLabelStyle = lineMismatch
    ? { fontWeight: 600, color: '#c2410c' }
    : { color: 'inherit' };
  const orderLabel = row.paragraphOrder != null ? ` (order ${row.paragraphOrder})` : '';

  const sharedTextareaStyle = {
    ...LINE_TEXTAREA_STYLE,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    resize: 'none',
    minHeight: MIN_TEXTAREA_HEIGHT_PX,
  };

  const measure = useCallback(() => {
    const src = srcRef.current;
    const tr = trRef.current;
    const idxS = idxSrcRef.current;
    const idxT = idxTrRef.current;
    if (!src || !tr || !idxS || !idxT) return;
    src.style.height = 'auto';
    tr.style.height = 'auto';
    const h = Math.max(src.scrollHeight, tr.scrollHeight, MIN_TEXTAREA_HEIGHT_PX);
    const nextHeight = `${h}px`;
    [src, tr, idxS, idxT].forEach((el) => {
      if (el.style.height !== nextHeight) {
        el.style.height = nextHeight;
      }
      el.style.overflow = 'hidden';
    });
  }, []);

  useAutosizeTextarea(measure, [row.sourceText, row.translationText]);

  return (
    <div
      style={{
        marginBottom: 24,
        paddingBottom: 20,
        borderBottom: '1px solid #e0e0e0',
      }}
    >
      <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>
        Paragraph {rowIndex + 1}
        {orderLabel}
      </h3>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '52px 7fr 52px 13fr',
          gridTemplateRows: 'auto minmax(0, auto)',
          columnGap: 12,
          rowGap: 6,
          alignItems: 'stretch',
        }}
      >
        <label
          className="form-hint"
          htmlFor={`sentence-idx-src-${row.paragraphId}`}
          style={{ gridRow: 1, gridColumn: 1, display: 'block', margin: 0, alignSelf: 'end' }}
        >
          #
        </label>
        <label
          htmlFor={`sentence-src-${row.paragraphId}`}
          style={{ gridRow: 1, gridColumn: 2, display: 'block', margin: 0 }}
        >
          Source (one sentence per line){' '}
          <span className="form-hint" style={lineCountLabelStyle}>
            ({sourceLineCount} {sourceLineCount === 1 ? 'line' : 'lines'})
          </span>
        </label>
        <label
          className="form-hint"
          htmlFor={`sentence-idx-tr-${row.paragraphId}`}
          style={{ gridRow: 1, gridColumn: 3, display: 'block', margin: 0, alignSelf: 'end' }}
        >
          #
        </label>
        <label
          htmlFor={`sentence-tr-${row.paragraphId}`}
          style={{ gridRow: 1, gridColumn: 4, display: 'block', margin: 0 }}
        >
          Translation{' '}
          <span className="form-hint" style={lineCountLabelStyle}>
            ({translationLineCount} {translationLineCount === 1 ? 'line' : 'lines'})
          </span>
          {lineMismatch && (
            <span className="form-hint" style={{ color: '#c2410c', marginLeft: 4 }}>
              Line counts must match
            </span>
          )}
        </label>
        <textarea
          ref={idxSrcRef}
          id={`sentence-idx-src-${row.paragraphId}`}
          readOnly
          rows={1}
          aria-label={`Paragraph ${rowIndex + 1} source line index`}
          value={sourceIndexText}
          style={{
            ...sharedTextareaStyle,
            gridRow: 2,
            gridColumn: 1,
            textAlign: 'right',
            background: '#f5f5f5',
            minWidth: 0,
          }}
        />
        <textarea
          ref={srcRef}
          id={`sentence-src-${row.paragraphId}`}
          value={row.sourceText}
          rows={1}
          onChange={(e) => updateRow(rowIndex, 'sourceText', e.target.value)}
          style={{ ...sharedTextareaStyle, gridRow: 2, gridColumn: 2, minWidth: 0 }}
        />
        <textarea
          ref={idxTrRef}
          id={`sentence-idx-tr-${row.paragraphId}`}
          readOnly
          rows={1}
          aria-label={`Paragraph ${rowIndex + 1} translation line index`}
          value={translationIndexText}
          style={{
            ...sharedTextareaStyle,
            gridRow: 2,
            gridColumn: 3,
            textAlign: 'right',
            background: '#f5f5f5',
            minWidth: 0,
          }}
        />
        <textarea
          ref={trRef}
          id={`sentence-tr-${row.paragraphId}`}
          value={row.translationText}
          rows={1}
          onChange={(e) => updateRow(rowIndex, 'translationText', e.target.value)}
          style={{ ...sharedTextareaStyle, gridRow: 2, gridColumn: 4, minWidth: 0 }}
        />
      </div>
    </div>
  );
};

const Sentence = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passagesLoading, setPassagesLoading] = useState(true);
  const [passagesError, setPassagesError] = useState('');
  const [passageId, setPassageId] = useState(passageIdFromLocationSearch);
  /** One UI row per passage paragraph (API order). */
  const [paragraphRows, setParagraphRows] = useState([]);
  const [paragraphLoading, setParagraphLoading] = useState(false);
  const [paragraphError, setParagraphError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasExistingSentences, setHasExistingSentences] = useState(false);

  const hasParagraphs = paragraphRows.length > 0;

  const updateRow = useCallback((index, field, value) => {
    setParagraphRows((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPassagesLoading(true);
      setPassagesError('');
      try {
        const res = await api.get('/data/passages', { params: { all: '1' } });
        if (!cancelled) {
          setPassages(res.data.passages || []);
          setPassagesError('');
        }
      } catch (err) {
        if (!cancelled) {
          setPassagesError(
            err.response?.data?.error ||
              err.response?.data?.details ||
              'Failed to load passages'
          );
          setPassages([]);
        }
      } finally {
        if (!cancelled) setPassagesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!passageId) {
      setParagraphRows([]);
      setParagraphError('');
      setHasExistingSentences(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setParagraphLoading(true);
      setParagraphError('');
      setMessage({ type: '', text: '' });
      try {
        const pid = parseInt(passageId, 10);
        const [pRes, sRes] = await Promise.all([
          api.get('/data/paragraphs', { params: { passageId: pid } }),
          api.get('/data/sentences', { params: { passageId: pid } }),
        ]);
        if (cancelled) return;

        const paragraphs = pRes.data?.paragraphs || [];
        const sentences = sRes.data?.sentences || [];
        setHasExistingSentences(sentences.length > 0);

        if (paragraphs.length === 0) {
          setParagraphRows([]);
          setParagraphError('No paragraph row for this passage.');
          setHasExistingSentences(false);
          return;
        }

        const byParagraph = new Map();
        for (const s of sentences) {
          const pidKey = s.paragraph_id;
          if (pidKey == null) continue;
          if (!byParagraph.has(pidKey)) byParagraph.set(pidKey, []);
          byParagraph.get(pidKey).push(s);
        }
        for (const [, list] of byParagraph) {
          list.sort(
            (a, b) =>
              (a.order ?? 0) - (b.order ?? 0) || (a.sentence_id ?? 0) - (b.sentence_id ?? 0)
          );
        }

        let stashed = null;
        try {
          const raw = sessionStorage.getItem(`paragraphTranslations:${pid}`);
          const parsed = raw ? JSON.parse(raw) : null;
          if (Array.isArray(parsed) && parsed.length === paragraphs.length) stashed = parsed;
        } catch {
          stashed = null;
        }

        const rows = paragraphs.map((p, idx) => {
          const list = byParagraph.get(p.paragraph_id) || [];
          let sourceText;
          let translationText;
          if (list.length > 0) {
            sourceText = list.map((s) => (s.value == null ? '' : String(s.value))).join('\n');
            translationText = list
              .map((s) => (s.translation == null ? '' : String(s.translation)))
              .join('\n');
          } else {
            sourceText = p.value == null ? '' : String(p.value);
            translationText = stashed ? String(stashed[idx] ?? '') : '';
          }
          return {
            paragraphId: p.paragraph_id,
            paragraphOrder: p.order,
            sourceText,
            translationText,
          };
        });

        setParagraphRows(rows);
        setParagraphError('');
      } catch (err) {
        if (!cancelled) {
          setParagraphRows([]);
          setParagraphError(
            err.response?.data?.error || err.response?.data?.details || 'Failed to load paragraphs'
          );
        }
      } finally {
        if (!cancelled) setParagraphLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [passageId]);

  const performSaveSentences = async ({ keepMessage = false } = {}) => {
    if (!keepMessage) setMessage({ type: '', text: '' });
    if (!passageId || !hasParagraphs) {
      setMessage({ type: 'error', text: 'Select a passage with at least one paragraph.' });
      return false;
    }

    const blocks = paragraphRows.map((row) => ({
      paragraphId: row.paragraphId,
      sourceLines: row.sourceText.split('\n'),
      translationLines: row.translationText.split('\n'),
    }));

    for (let i = 0; i < blocks.length; i += 1) {
      const b = blocks[i];
      if (b.sourceLines.length !== b.translationLines.length) {
        setMessage({
          type: 'error',
          text: `Paragraph ${i + 1}: source has ${b.sourceLines.length} line(s), translation has ${b.translationLines.length}. Match line counts within each paragraph.`,
        });
        return false;
      }
      if (b.sourceLines.length === 0) {
        setMessage({
          type: 'error',
          text: `Paragraph ${i + 1}: at least one source line is required.`,
        });
        return false;
      }
    }

    setSubmitLoading(true);
    try {
      const res = await api.post('/data/sentences/save-for-passage', {
        passageId: parseInt(passageId, 10),
        blocks,
      });
      const n = res.data?.updated ?? res.data?.inserted;
      setMessage({
        type: 'success',
        text:
          n != null
            ? `${res.data?.message || 'Saved'} (${n} sentence row(s))`
            : res.data?.message || 'Saved',
      });
      setHasExistingSentences(true);
      return true;
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.error || err.response?.data?.details || 'Failed to save sentences',
      });
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    await performSaveSentences();
  };

  const handleSaveAll = async ({ goToPassage = false } = {}) => {
    const ok = await performSaveSentences({ keepMessage: true });
    if (!ok) return;
    const pid = parseInt(passageId, 10);
    setSubmitLoading(true);
    const steps = ['Sentences saved'];
    try {
      // Short sentences: keep existing ones; otherwise split sentences at Chinese punctuation.
      const ssRes = await api.get('/data/short-sentences/editor', { params: { passageId: pid } });
      if (ssRes.data?.hasShortSentences) {
        steps.push('short sentences already exist (kept)');
      } else {
        const items = (ssRes.data?.sentences || []).map((s) => ({
          sentenceId: s.sentence_id,
          text: formatChinesePunctuation(s.value == null ? '' : String(s.value)).formatted || '',
        }));
        await api.post('/data/short-sentences/save-for-passage', { passageId: pid, items });
        steps.push('short sentences saved');
      }

      // Words: keep existing ones; otherwise save the generated default rows.
      const wRes = await api.get('/data/words/editor', { params: { passageId: pid } });
      if (wRes.data?.fromDb) {
        steps.push('words already exist (kept)');
      } else {
        const rows = (wRes.data?.rows || []).map((r) => ({
          short_sentence_id: r.short_sentence_id,
          value: r.value,
          part_of_speech: r.part_of_speech,
          meaning_0: r.meaning_0,
          meaning_1: r.meaning_1,
          meaning_2: r.meaning_2,
          tone: r.tone,
          synonym_0: r.synonym_0,
          synonym_1: r.synonym_1,
          synonym_2: r.synonym_2,
          synonym_3: r.synonym_3,
          synonym_4: r.synonym_4,
          synonym_5: r.synonym_5,
        }));
        if (rows.length === 0) {
          steps.push('no word rows to save');
        } else {
          await api.post('/data/words/save-for-passage', { passageId: pid, rows });
          steps.push(`words saved (${rows.length} row(s))`);
        }
      }
      setMessage({ type: 'success', text: steps.join('; ') });
      if (goToPassage) navigate('/passage');
    } catch (err) {
      setMessage({
        type: 'error',
        text: `${steps.join('; ')}; then failed: ${
          err.response?.data?.error || err.response?.data?.details || err.message
        }`,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSaveAndGoShortSentence = async () => {
    const ok = await performSaveSentences();
    if (ok && passageId) navigate(`/short-sentence?passageId=${encodeURIComponent(passageId)}`);
  };

  const handleCutSentences = () => {
    setParagraphRows((prev) =>
      prev.map((row) => ({
        ...row,
        sourceText: cutTextBySentencePunctuation(row.sourceText),
        translationText: cutTextBySentencePunctuation(row.translationText),
      }))
    );
    setMessage({ type: '', text: '' });
  };

  return (
    <div className="dashboard">
      <DashboardHeader title="Sentence editor" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 1500, margin: '0 auto' }}>
          {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

          <div className="form-section">
            <h2>Sentences by line</h2>

            <div className="form-group">
              <PassagePicker
                inputId="sentence-passage-picker"
                label={
                  <>
                    Passage <span className="required">*</span>
                  </>
                }
                passages={passages}
                passagesLoading={passagesLoading}
                passagesError={passagesError}
                value={passageId}
                onChange={setPassageId}
              />
            </div>

            {passageId && paragraphLoading && (
              <div className="loading-state">Loading paragraphs…</div>
            )}
            {passageId && !paragraphLoading && paragraphError && (
              <div className="message error">{paragraphError}</div>
            )}

            {passageId && !paragraphLoading && hasParagraphs && (
              <form onSubmit={handleSave}>
                <p className="form-hint" style={{ marginBottom: 16 }}>
                  Each passage paragraph has its own pair of columns. Within a paragraph, one line
                  = one sentence; left and right line counts must match for that paragraph. Passage-wide
                  sentence <code>order</code> runs from top to bottom across paragraphs.
                </p>



                {paragraphRows.map((row, index) => (
                  <SentenceParagraphRow
                    key={row.paragraphId}
                    row={row}
                    rowIndex={index}
                    updateRow={updateRow}
                  />
                ))}

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                  <button
                    type="button"
                    className="submit-button"
                    disabled={submitLoading || paragraphLoading || !hasParagraphs}
                    onClick={handleCutSentences}
                  >
                    Cut sentence
                  </button>
                </div>

                <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={submitLoading || !hasParagraphs}
                  >
                    {submitLoading
                      ? 'Saving…'
                      : hasExistingSentences
                        ? 'Save sentences'
                        : 'Insert sentences'}
                  </button>
                  <button
                    type="button"
                    className="submit-button"
                    disabled={submitLoading || !hasParagraphs}
                    onClick={handleSaveAndGoShortSentence}
                  >
                    {submitLoading
                      ? 'Saving…'
                      : hasExistingSentences
                        ? 'Save sentences and go to Short sentence'
                        : 'Insert sentences and go to Short sentence'}
                  </button>
                  <button
                    type="button"
                    className="submit-button"
                    disabled={submitLoading || !hasParagraphs}
                    onClick={() => handleSaveAll({ goToPassage: true })}
                  >
                    {submitLoading
                      ? 'Saving…'
                      : 'Save sentence, short sentence and word and insert new passage'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sentence;
